import { take, takeLatest, put, call, fork, cancel, select, all } from 'redux-saga/effects';

import { authActionTypes } from '../auth/auth.types';
import { collabActionTypes } from './collab.types';
import { socketActionTypes, SOCKET_STATUS } from '../socket/socket.types';
import { socketStatusChanged, socketConnectFailed } from '../socket/socket.actions';
import {
    joinRoom,
    roomJoined,
    roomLeft,
    joinFailed,
    docUnauthorized,
    collabReset,
} from './collab.actions';
import { selectCurrentRoom } from './collab.selectors';
import { createSocketChannel, SOCKET_CHANNEL_EVENTS } from '../socket/socket.channel';
import { handleSessionExpired } from '../socket/socket.sessionExpired';
import {
    collaborationSocket,
    emitWithAck,
    emitWithRetry,
    connectNamespace,
    disconnectNamespace,
    NAMESPACES,
} from '../../lib/socket';
import { sendSyncStep1, applyJoinMembers } from '../../lib/yjs';

const NAMESPACE = NAMESPACES.COLLABORATION;

// Backend D1 events — see docs/collaboration/frontend-integration.md §3.
const CUSTOM_EVENTS = ['doc:unauthorized'];

function* runCollabChannel() {
    const channel = yield call(createSocketChannel, collaborationSocket, CUSTOM_EVENTS);
    try {
        while (true) {
            const frame = yield take(channel);
            switch (frame.event) {
                case SOCKET_CHANNEL_EVENTS.CONNECT:
                    yield put(socketStatusChanged(NAMESPACE, SOCKET_STATUS.CONNECTED));
                    yield call(handleReconnect);
                    break;
                case SOCKET_CHANNEL_EVENTS.DISCONNECT:
                    yield put(socketStatusChanged(NAMESPACE, SOCKET_STATUS.DISCONNECTED));
                    break;
                case SOCKET_CHANNEL_EVENTS.RECONNECT_ATTEMPT:
                    yield put(socketStatusChanged(NAMESPACE, SOCKET_STATUS.RECONNECTING));
                    break;
                case SOCKET_CHANNEL_EVENTS.CONNECT_ERROR: {
                    const message = (frame.data && frame.data.message) || 'connect_error';
                    yield put(socketConnectFailed(NAMESPACE, message));
                    if (message === 'authentication_failed') {
                        yield call(handleSessionExpired);
                    }
                    break;
                }
                case SOCKET_CHANNEL_EVENTS.SESSION_EXPIRED:
                    yield call(handleSessionExpired);
                    break;
                case 'doc:unauthorized':
                    yield put(docUnauthorized(frame.data && frame.data.reason));
                    break;
                default:
                    break;
            }
        }
    } finally {
        channel.close();
    }
}

// On socket reconnect, re-issue diagram:join for the same diagram if the
// user was in one. The server holds no per-user state across socket
// connections (backend doc §8.1).
export function* handleReconnect() {
    const currentRoom = yield select(selectCurrentRoom);
    if (currentRoom) {
        yield put(joinRoom(currentRoom));
    }
}

export function* handleJoinRoom({ payload }) {
    const { diagramId } = payload;

    if (!collaborationSocket.connected) {
        yield put(socketStatusChanged(NAMESPACE, SOCKET_STATUS.CONNECTING));
        connectNamespace(collaborationSocket);
        yield take((a) =>
            a.type === socketActionTypes.SOCKET_STATUS_CHANGED &&
            a.payload.namespace === NAMESPACE &&
            (a.payload.status === SOCKET_STATUS.CONNECTED || a.payload.status === SOCKET_STATUS.DISCONNECTED)
        );
        if (!collaborationSocket.connected) {
            yield put(joinFailed('socket_disconnected'));
            return;
        }
    }

    try {
        // Backend doc §4.2 — ack is { ok, role, members: Uint8Array }.
        // diagram:join is idempotent server-side: re-joining the same room
        // returns the current state. emitWithRetry safely bridges transient
        // disconnects.
        const ack = yield call(
            emitWithRetry,
            collaborationSocket,
            'diagram:join',
            { diagramId },
        );

        // Apply the binary awareness snapshot from the ack.
        if (ack && ack.members) {
            applyJoinMembers(ack.members);
        }

        yield put(roomJoined({ diagramId, role: ack && ack.role }));

        // Trigger the y-protocols/sync handshake (backend doc §4.4).
        sendSyncStep1();
    } catch (e) {
        yield put(joinFailed(e.message));
    }
}

export function* handleLeaveRoom() {
    try {
        if (collaborationSocket.connected) {
            // Fire-and-forget: emitWithAck (no retry) — we're tearing down.
            yield call(emitWithAck, collaborationSocket, 'diagram:leave', {});
        }
    } catch (_) {
        // ignore — we're leaving anyway
    }
    yield put(roomLeft());
    // Intentionally keep the socket connected so the next diagram:join
    // (client-side navigation) doesn't produce a DISCONNECTED flash.
    // The socket is only disconnected on sign-out (collabNamespaceSaga).
}

export function* collabNamespaceSaga() {
    while (true) {
        yield take(authActionTypes.SIGN_IN_SUCCESS);

        const channelTask = yield fork(runCollabChannel);
        const joinTask = yield takeLatest(collabActionTypes.COLLAB_JOIN_ROOM, handleJoinRoom);
        const leaveTask = yield takeLatest(collabActionTypes.COLLAB_LEAVE_ROOM, handleLeaveRoom);

        yield take(authActionTypes.SIGN_OUT_SUCCESS);
        yield cancel(channelTask);
        yield cancel(joinTask);
        yield cancel(leaveTask);

        disconnectNamespace(collaborationSocket);
        yield put(collabReset());
        yield put(socketStatusChanged(NAMESPACE, SOCKET_STATUS.IDLE));
    }
}

export function* collabSagas() {
    yield all([call(collabNamespaceSaga)]);
}
